import React, {useEffect, useState} from 'react'
import {
  getConfigurationByName,
  updateConfigurationByConfigID,
} from '../../../../../../API/api-endpoint'
import ToastUtils, {ErrorToastUtils} from '../../../../../../utils/ToastUtils'

const RandomCallSuggestionsPlugin = () => {
  const [configID, setConfigId] = useState(0)
  const [reviewShowPopupMinutes, setReviewShowPopupMinutes] = useState<number>(2)
  const [suggestions, setSuggestions] = useState<any>({
    '1': [],
    '2': [],
    '3': [],
    '4': [],
    '5': [],
  })

  // Inputs for adding new suggestions to each star
  const [newInputs, setNewInputs] = useState<any>({
    '1': '',
    '2': '',
    '3': '',
    '4': '',
    '5': '',
  })

  useEffect(() => {
    getConfiguration()
  }, [])

  const getConfiguration = async () => {
    let result = await getConfigurationByName('random-call-review-suggestions')
    if (result.success && result.data) {
      let parsedData = JSON.parse(result.data.values)
      setConfigId(result.data.id)
      setReviewShowPopupMinutes(parsedData?.reviewShowPopupMinutes ?? 2)
      setSuggestions({
        '1': parsedData?.suggestions?.['1'] ?? [],
        '2': parsedData?.suggestions?.['2'] ?? [],
        '3': parsedData?.suggestions?.['3'] ?? [],
        '4': parsedData?.suggestions?.['4'] ?? [],
        '5': parsedData?.suggestions?.['5'] ?? [],
      })
    }
  }

  const handlePopupMinutesChange = (event: any) => {
    const value = Number(event.target.value)
    setReviewShowPopupMinutes(value)
  }

  const handlePopupMinutesBlur = () => {
    saveConfiguration(reviewShowPopupMinutes, suggestions)
  }

  const handleInputChange = (star: string, value: string) => {
    setNewInputs({
      ...newInputs,
      [star]: value,
    })
  }

  const addSuggestion = (star: string) => {
    const text = newInputs[star].trim()
    if (!text) return

    const updatedList = [...suggestions[star], text]
    const updatedSuggestions = {
      ...suggestions,
      [star]: updatedList,
    }

    setSuggestions(updatedSuggestions)
    setNewInputs({
      ...newInputs,
      [star]: '',
    })

    saveConfiguration(reviewShowPopupMinutes, updatedSuggestions)
  }

  const deleteSuggestion = (star: string, indexToDelete: number) => {
    const updatedList = suggestions[star].filter((_: any, idx: number) => idx !== indexToDelete)
    const updatedSuggestions = {
      ...suggestions,
      [star]: updatedList,
    }

    setSuggestions(updatedSuggestions)
    saveConfiguration(reviewShowPopupMinutes, updatedSuggestions)
  }

  const saveConfiguration = async (minutes: number, currentSuggestions: any) => {
    const valuesPayload = {
      reviewShowPopupMinutes: minutes,
      suggestions: currentSuggestions,
    }

    let result = await updateConfigurationByConfigID(configID, valuesPayload, null)
    if (result.success) {
      ToastUtils({type: 'success', message: 'Suggestions Updated SuccessFully'})
    } else {
      ErrorToastUtils()
    }
  }

  const renderStars = (count: number) => {
    return Array.from({length: 5}, (_, idx) => (
      <i
        key={idx}
        className={`fa-solid fa-star fs-4 ${idx < count ? 'text-warning' : 'text-gray-300'}`}
      ></i>
    ))
  }

  return (
    <div className='d-flex flex-column gap-8'>
      {/* Show Popup Interval Card */}
      <div className='card shadow-sm'>
        <div className='card-header border-0 bg-light-primary py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold text-primary fs-3'>
              <i className='fa-solid fa-clock me-2 text-primary'></i>
              Review Popup Timing
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Define when to prompt users for a call review
            </span>
          </h3>
        </div>
        <div className='card-body'>
          <div className='col-md-6'>
            <label className='form-label fw-bold text-dark fs-6'>Show Review Popup after (Minutes)</label>
            <div className='input-group'>
              <input
                type='number'
                className='form-control form-control-solid'
                placeholder='2'
                min={1}
                value={reviewShowPopupMinutes}
                onChange={handlePopupMinutesChange}
                onBlur={handlePopupMinutesBlur}
              />
              <span className='input-group-text border-0 fw-bold'>minutes</span>
            </div>
            <span className='text-muted fs-7 d-block mt-1'>
              Prompt the user to review the call quality after X minutes of active match calling
            </span>
          </div>
        </div>
      </div>

      {/* Review Suggestions Card */}
      <div className='card shadow-sm'>
        <div className='card-header border-0 bg-light-warning py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold text-warning fs-3'>
              <i className='fa-solid fa-star-half-stroke me-2 text-warning'></i>
              Star-wise Review Suggestions Board
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Configure predefined quick suggestions tags that users can pick depending on their rating
            </span>
          </h3>
        </div>

        <div className='card-body d-flex flex-column gap-6'>
          {['1', '2', '3', '4', '5'].map((star) => (
            <div key={star} className='p-5 border border-dashed rounded bg-light bg-opacity-50'>
              <div className='row align-items-center g-4'>
                {/* Stars Column */}
                <div className='col-lg-3 d-flex align-items-center gap-2'>
                  <span className='fw-bold text-dark fs-5 me-2'>{star} Star Suggestions</span>
                  <div className='d-flex gap-1'>{renderStars(Number(star))}</div>
                </div>

                {/* Suggestions badges / input */}
                <div className='col-lg-9'>
                  <div className='d-flex flex-column gap-4'>
                    {/* Tags Container */}
                    <div className='d-flex flex-wrap gap-2'>
                      {suggestions[star].length === 0 ? (
                        <span className='text-muted fs-7 italic'>No suggestions added yet. Add some below!</span>
                      ) : (
                        suggestions[star].map((tag: string, index: number) => (
                          <span
                            key={index}
                            className='badge badge-lg bg-light-primary text-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 rounded-pill border border-primary border-opacity-20 shadow-xs'
                          >
                            {tag}
                            <button
                              type='button'
                              className='btn btn-icon btn-xs btn-active-light-danger border-0 bg-transparent p-0 ms-1'
                              onClick={() => deleteSuggestion(star, index)}
                              style={{lineHeight: 1}}
                            >
                              <i className='fa-solid fa-xmark text-danger fs-6'></i>
                            </button>
                          </span>
                        ))
                      )}
                    </div>

                    {/* Add Suggestion Input group */}
                    <div className='d-flex gap-2 col-md-8 col-lg-6'>
                      <input
                        type='text'
                        className='form-control form-control-sm form-control-solid'
                        placeholder={`Add quick feedback tag for ${star} Star...`}
                        value={newInputs[star]}
                        onChange={(e) => handleInputChange(star, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addSuggestion(star)
                          }
                        }}
                      />
                      <button
                        type='button'
                        className='btn btn-sm btn-primary d-flex align-items-center px-4 gap-1'
                        onClick={() => addSuggestion(star)}
                      >
                        <i className='fa-solid fa-plus fs-6'></i>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default RandomCallSuggestionsPlugin
